import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import Grid2 from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { DelayAreaSelector } from "@/shared/selectors/DelayAreaSelector";
import { Checkbox, FormControlLabel, Paper } from "@mui/material";
import { useCategories } from "../CatalogCategoriesPage";
import { useCategoriesOptionsFunctions } from "../hooks/useCategoriesOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { DelayCategory } from "../models/CatalogCategory";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

export const TableCategories = ({
	categoryId,
}: {
	categoryId: string | undefined;
}) => {
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] = useState<DelayCategory | null>(
		null,
	);
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [error, setError] = useState("");
	const [includeInactives, setIncludeInactive] = useState(false);
	const [delayAreaAssetId, setDelayAreaAssetId] = useState<number | null>(null);

	const { data: rows, isLoading } = useCategories({
		includeInactives: includeInactives,
		delayAreaAssetId: delayAreaAssetId,
		onError: (error) => setError(error.message),
	});

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useCategoriesOptionsFunctions({
		setSelectedRowData,
		setShowModal,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: any) => {
		setSelectedRowData(event.data);
		setShowModal("update");
	};

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "categoriesContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "categoriesContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					spacing={1}
					direction={"column"}
				>
					<Grid2>
						<Typography variant="h5" fontWeight={600}>
							Delays Categories
						</Typography>
					</Grid2>
					<Grid2 container spacing={1} alignItems="center">
						<Grid2 size={{ xs: 12, md: 2 }}>
							<DelayAreaSelector
								label="Assets"
								value={delayAreaAssetId}
								onChange={setDelayAreaAssetId}
								showAllOption
							/>
						</Grid2>
						<Grid2>
							<FormControlLabel
								control={
									<Checkbox
										checked={includeInactives}
										color="primary"
										onChange={(e) => setIncludeInactive(!includeInactives)}
									/>
								}
								label="Inactive"
							/>
						</Grid2>
					</Grid2>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper elevation={2} style={{ height: "64vh", width: "100%" }}>
							<AgGridReact<DelayCategory>
								loading={isLoading}
								rowData={rows || []}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								rowHeight={34}
								headerHeight={30}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								onGridReady={(params) => setGridApi(params.api)}
								loadingOverlayComponent={CenteredLazyLoading}
								getRowId={(params) => `${params.data.DelayCategoryId}`}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
								}}
							/>
						</Paper>
					</Grid2>
					<FooterButton
						label="CATEGORY"
						showCreateModal={showModal === "create"}
						setShowCreateModal={(showCreateModal) =>
							showCreateModal ? setShowModal("create") : setShowModal("")
						}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<UpsertDeleteModal
				delayCategory={selectedRowData}
				modal={showModal}
				delayAreaAssetId={delayAreaAssetId}
				onHide={() => {
					setShowModal("");
				}}
			/>
		</>
	);
};
